/*
 * Copyright © HatioLab Inc. All rights reserved.
 */

import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { Component, RealObjectGroup } from '@hatiolab/things-scene'

import { Rack } from './rack.js'
import type { RackTable } from './rack-table.js'

const DEFAULT_FRAME_COLOR = 0x8a8a8a

export class RackTable3d extends RealObjectGroup {
  private _frameMaterial?: THREE.MeshStandardMaterial

  build() {
    super.build()

    this.createRacks()
  }

  get position() {
    const { zPos = 0 } = this.component.state

    return {
      x: this.cx || 0,
      y: zPos,
      z: this.cy || 0
    }
  }

  private createFrameMaterial(): THREE.MeshStandardMaterial {
    this._frameMaterial?.dispose()

    const { strokeStyle } = this.component.state
    const color = strokeStyle && typeof strokeStyle === 'string' ? strokeStyle : DEFAULT_FRAME_COLOR

    this._frameMaterial = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.35,
      metalness: 0.85
    })

    return this._frameMaterial
  }

  createRacks() {
    const { rotation = 0, shelfLocations, shelves = 1 } = this.component.state

    this.object3d.rotation.y = -rotation

    const racks = (this.component as unknown as RackTable).components
      .map((cell: Component) => {
        const { shelfLocations: shelfLoc = shelfLocations, isEmpty } = cell.state

        if (!isEmpty) {
          cell.setState('shelfLocations', shelfLoc)

          const rack = new Rack(cell)
          cell._realObject = rack // 중복 생성 방지: addObject 재귀에서 skip

          rack.update()
          this.object3d.add(rack.object3d)

          return rack
        }
        return
      })
      .filter((rack): rack is Rack => !!rack)

    this.mergeAndAddRackCommonObjects(racks)
  }

  mergeAndAddRackCommonObjects(racks: Rack[]) {
    const framesGeometries: THREE.BufferGeometry[] = []
    const boardsGeometries: THREE.BufferGeometry[] = []

    if (racks.length > 0) {
      racks.forEach(rack => {
        const geometry = rack.frame

        if (!geometry) {
          return
        }

        geometry.translate(rack.position.x, rack.position.y, rack.position.z)
        geometry.scale(rack.scale.x, rack.scale.y, rack.scale.z)
        framesGeometries.push(geometry)
      })

      if (framesGeometries.length > 0) {
        const frameMesh = new THREE.Mesh(BufferGeometryUtils.mergeGeometries(framesGeometries), this.createFrameMaterial())
        this.object3d.add(frameMesh)
      }

      racks.forEach(rack => {
        const geometry = rack.board

        if (!geometry) {
          return
        }

        geometry.translate(rack.position.x, rack.position.y, rack.position.z)
        geometry.scale(rack.scale.x, rack.scale.y, rack.scale.z)
        boardsGeometries.push(geometry)
      })

      if (boardsGeometries.length > 0) {
        const material = Rack.boardMaterial
        material.opacity = 0.5
        material.transparent = true

        const boardMesh = new THREE.Mesh(BufferGeometryUtils.mergeGeometries(boardsGeometries), material)

        this.object3d.add(boardMesh)
      }
    }
  }

  dispose() {
    this._frameMaterial?.dispose()
    this._frameMaterial = undefined

    super.dispose()
  }

  updateAlpha() {}
}
